import { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Boxes, ChevronDown, ChevronRight, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import RegionFilter from "@/components/RegionFilter";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const OPEN_STAGES = ["New", "Contacted", "Demo Scheduled", "Demo Completed", "Quotation Sent"];
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const LeadList = ({ row }) => (
  <ul className="divide-y divide-slate-100 bg-slate-50/60" data-testid={`demand-leads-${row.catalog_id}`}>
    {row.leads.map((l) => (
      <li key={l.id} className="pl-10 pr-5 py-2 flex items-center justify-between gap-3 text-xs">
        <div className="min-w-0">
          <Link to={`/leads?lead=${l.id}`} className="font-medium text-indigo-700 hover:underline inline-flex items-center gap-1" data-testid={`demand-lead-link-${row.catalog_id}-${l.id}`}>
            {l.company_name || "—"} <ExternalLink className="w-3 h-3" />
          </Link>
          <div className="text-slate-500 truncate">{[l.stage, l.region_name, l.owner_name].filter(Boolean).join(" · ")}</div>
        </div>
        <span className="font-semibold text-slate-800 shrink-0">× {l.quantity}</span>
      </li>
    ))}
  </ul>
);

const ModelRow = ({ row, open, onToggle }) => (
  <Fragment>
    <button type="button" onClick={onToggle} className="w-full px-5 py-3 flex items-center gap-3 text-left hover:bg-slate-50 transition-colors" data-testid={`demand-row-${row.catalog_id}`}>
      {open ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
      <div className="min-w-0 flex-1">
        <div className="font-medium text-sm text-slate-900 truncate">{row.model_name}</div>
        <div className="text-xs text-slate-500 truncate">{row.manufacturer} · {row.lead_count} lead{row.lead_count === 1 ? "" : "s"}</div>
      </div>
      <div className="text-right shrink-0">
        <div className="font-heading text-lg font-semibold text-slate-900 leading-none" data-testid={`demand-units-${row.catalog_id}`}>{row.units}</div>
        <div className="text-[11px] text-slate-500 mt-1">{inr(row.value)}</div>
      </div>
    </button>
    {open && <LeadList row={row} />}
  </Fragment>
);

export default function PipelineDemandWidget() {
  const [region, setRegion] = useState("");
  const [stage, setStage] = useState("");
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    const p = new URLSearchParams();
    if (region) p.set("region_id", region);
    if (stage) p.set("stage", stage);
    api.get(`/reports/pipeline-demand?${p}`).then((r) => setData(r.data)).catch(() => setData({ models: [], total_units: 0, total_value: 0, lead_count: 0 }));
  }, [region, stage]);

  const models = data?.models || [];
  return (
    <section className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="pipeline-demand-widget">
      <div className="px-5 py-4 border-b border-slate-100 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md flex items-center justify-center bg-indigo-50 text-indigo-700"><Boxes className="w-4 h-4" /></div>
          <div>
            <div className="font-heading font-semibold text-slate-900">Pipeline demand</div>
            <div className="text-xs text-slate-500">Units wanted per model across open leads</div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <RegionFilter value={region} onChange={setRegion} testId="demand-region-filter" />
          <Select value={stage || "all"} onValueChange={(v) => setStage(v === "all" ? "" : v)}>
            <SelectTrigger className="h-9 w-44 text-xs" data-testid="demand-stage-filter"><SelectValue placeholder="All open stages" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All open stages</SelectItem>
              {OPEN_STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      {data === null ? (
        <div className="p-5"><div className="h-16 bg-slate-50 rounded animate-pulse" /></div>
      ) : models.length === 0 ? (
        <div className="p-6 text-center text-sm text-slate-500" data-testid="demand-empty">No potential machines on open leads for this filter.</div>
      ) : (
        <>
          <div className="px-5 py-3 grid grid-cols-3 gap-3 border-b border-slate-100 text-xs text-slate-500">
            <div><div className="font-heading text-2xl font-semibold text-slate-900" data-testid="demand-total-units">{data.total_units}</div>units</div>
            <div><div className="font-heading text-2xl font-semibold text-slate-900" data-testid="demand-total-models">{models.length}</div>models</div>
            <div className="col-span-3 sm:col-span-1"><div className="font-heading text-xl sm:text-2xl font-semibold text-slate-900" data-testid="demand-total-value">{inr(data.total_value)}</div>at list price · {data.lead_count} lead{data.lead_count === 1 ? "" : "s"}</div>
          </div>
          <div className="divide-y divide-slate-100">
            {models.map((m) => (
              <ModelRow key={m.catalog_id} row={m} open={!!expanded[m.catalog_id]}
                onToggle={() => setExpanded((p) => ({ ...p, [m.catalog_id]: !p[m.catalog_id] }))} />
            ))}
          </div>
        </>
      )}
    </section>
  );
}
